import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, DemoBadge } from "@/components/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Loader2, Compass, ExternalLink, CheckCircle2, XCircle, Route,
} from "lucide-react";

const PRODUCT_TYPES = ["MAPD", "PDP", "SNP", "MEDSUPP"];

const FIELD_DEFS = {
  providers: [
    { key: "name", label: "Name", type: "text", required: true },
    { key: "code", label: "Code", type: "text", required: true },
    { key: "integration_type", label: "Integration type", type: "select", default: "purl_deeplink",
      options: ["purl_deeplink", "deep_link", "api", "embedded", "direct_carrier"] },
    { key: "status", label: "Status", type: "select", default: "active", options: ["active", "inactive"] },
    { key: "supports_product_types", label: "Supported product types", type: "multiselect", options: PRODUCT_TYPES, default: [] },
    { key: "prefill_supported", label: "Supports API prefill (future)", type: "checkbox", default: false },
    { key: "notes", label: "Notes", type: "textarea" },
  ],
  fmos: [
    { key: "name", label: "Name", type: "text", required: true },
    { key: "code", label: "Code", type: "text", required: true },
    { key: "type", label: "Type", type: "select", default: "fmo", options: ["fmo", "agency"] },
    { key: "notes", label: "Notes", type: "textarea" },
  ],
  producers: [
    { key: "name", label: "Producer name", type: "text", required: true },
    { key: "npn", label: "NPN", type: "text" },
    { key: "status", label: "Status", type: "select", default: "active", options: ["active", "inactive"] },
    { key: "user_id", label: "Linked advisor account", type: "ref", refFrom: "advisors" },
  ],
  profiles: [
    { key: "producer_id", label: "Producer", type: "ref", refFrom: "producers", required: true },
    { key: "provider_id", label: "Enrollment provider", type: "ref", refFrom: "providers", required: true },
    { key: "fmo_id", label: "FMO / Agency", type: "ref", refFrom: "fmos" },
    { key: "provider_account_ref", label: "Provider account ref", type: "text" },
    { key: "contracting_status", label: "Contracting", type: "select", default: "contracted",
      options: ["contracted", "pending", "terminated"] },
    { key: "states_licensed", label: "States licensed (comma)", type: "csv", hint: "Empty = all states" },
    { key: "product_type_access", label: "Product type access", type: "multiselect", options: PRODUCT_TYPES, default: [] },
    { key: "carrier_access", label: "Carrier access (comma)", type: "csv", hint: "Empty = all carriers" },
    { key: "priority", label: "Priority", type: "number", default: 0 },
    { key: "active", label: "Active", type: "checkbox", default: true },
  ],
  destinations: [
    { key: "enrollment_profile_id", label: "Enrollment profile", type: "ref", refFrom: "profiles", required: true },
    { key: "label", label: "Label", type: "text", required: true },
    { key: "destination_type", label: "Destination type", type: "select", default: "purl",
      options: ["purl", "deep_link", "api_endpoint"] },
    { key: "landing_type", label: "Landing type", type: "select", default: "general_marketplace",
      options: ["exact_plan_link", "general_marketplace"],
      hint: "exact_plan_link = lands on the specific plan; general_marketplace = agent selects the plan there" },
    { key: "url", label: "URL / PURL (configuration, not code)", type: "text" },
    { key: "match_states", label: "Match states (comma)", type: "csv", hint: "Empty = all" },
    { key: "match_product_types", label: "Match product types", type: "multiselect", options: PRODUCT_TYPES, default: [] },
    { key: "match_carriers", label: "Match carriers (comma)", type: "csv", hint: "Empty = all" },
    { key: "priority", label: "Priority", type: "number", default: 0 },
    { key: "active", label: "Active", type: "checkbox", default: true },
  ],
};

const ENTITY_META = {
  providers: { title: "Enrollment Providers", singular: "provider" },
  fmos: { title: "FMOs & Agencies", singular: "FMO" },
  producers: { title: "Producers", singular: "producer" },
  profiles: { title: "Enrollment Profiles", singular: "profile" },
  destinations: { title: "Destinations (PURLs / Deep-links)", singular: "destination" },
};

export default function EnrollmentRouter() {
  const [data, setData] = useState({ providers: [], fmos: [], producers: [], profiles: [], destinations: [], advisors: [] });
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, entity: null, initial: null });

  const loadAll = useCallback(async () => {
    const keys = ["providers", "fmos", "producers", "profiles", "destinations"];
    const results = await Promise.all(keys.map((k) => api.get(`/admin/enrollment/${k}`)));
    const advisorsRes = await api.get("/admin/advisors");
    const next = Object.fromEntries(keys.map((k, i) => [k, results[i].data]));
    next.advisors = advisorsRes.data.map((u) => ({ id: u.id, name: `${u.name} — ${u.email}` }));
    setData(next);
  }, []);

  useEffect(() => { loadAll().finally(() => setLoading(false)); }, [loadAll]);

  const refLabel = (entity, id) => {
    if (!id) return "—";
    const item = (data[entity] || []).find((x) => x.id === id);
    if (!item) return id;
    return item.name || item.label || item.provider_account_ref || id;
  };

  const remove = async (entity, id) => {
    try { await api.delete(`/admin/enrollment/${entity}/${id}`); toast.success("Deleted"); loadAll(); }
    catch { toast.error("Unable to delete"); }
  };

  if (loading) return <div className="p-6 sm:p-12 max-w-7xl mx-auto"><Skeleton className="h-96 w-full rounded-xl" /></div>;

  const columns = {
    providers: (r) => [r.name, r.code, r.integration_type, r.status, (r.supports_product_types || []).join(", ") || "all"],
    fmos: (r) => [r.name, r.code, r.type],
    producers: (r) => [r.name, r.npn || "—", r.status,
      (data.advisors.find((a) => a.id === r.user_id)?.name) || (r.user_id ? "linked" : "—")],
    profiles: (r) => [refLabel("producers", r.producer_id), refLabel("providers", r.provider_id),
      refLabel("fmos", r.fmo_id), (r.states_licensed || []).join(",") || "all",
      (r.product_type_access || []).join(",") || "all", r.priority, r.active ? "yes" : "no"],
    destinations: (r) => [r.label, refLabel("profiles", r.enrollment_profile_id), r.destination_type,
      r.landing_type || "general_marketplace",
      (r.match_states || []).join(",") || "all", (r.match_product_types || []).join(",") || "all",
      r.url ? "set" : "—", r.priority, r.active ? "yes" : "no"],
  };
  const heads = {
    providers: ["Name", "Code", "Integration", "Status", "Product types", ""],
    fmos: ["Name", "Code", "Type", ""],
    producers: ["Name", "NPN", "Status", "Linked advisor", ""],
    profiles: ["Producer", "Provider", "FMO", "States", "Products", "Priority", "Active", ""],
    destinations: ["Label", "Profile", "Type", "Landing", "States", "Products", "URL", "Priority", "Active", ""],
  };

  return (
    <div className="p-6 sm:p-10 lg:p-12 max-w-7xl mx-auto animate-rise">
      <PageHeader
        eyebrow="Enrollment integration layer"
        title="Enrollment Router"
        subtitle="Provider-neutral routing to approved enrollment destinations. SunFire is the first provider; PURLs/deep-links are stored as configuration, not code. API & direct-carrier integrations plug in here later."
        action={<DemoBadge className="mt-1" />}
      />

      <div className="rounded-xl bg-navy-800 p-5 mb-6 flex items-start gap-3">
        <Compass className="w-5 h-5 text-gold shrink-0 mt-0.5" />
        <p className="text-sm text-mist-200 leading-relaxed">
          Nauticus owns the household experience up to enrollment, then routes the consumer to the correct
          <span className="text-white font-semibold"> provider → account → destination</span> based on producer,
          agency, carrier, state, product type, and contracting. A producer can hold multiple profiles across
          FMOs, accounts, PURL sets, and licenses.
        </p>
      </div>

      <Tabs defaultValue="tester">
        <TabsList className="bg-white border border-mist-200 flex-wrap h-auto" data-testid="enrollment-tabs">
          <TabsTrigger value="tester" data-testid="tab-tester">Route Tester</TabsTrigger>
          <TabsTrigger value="providers" data-testid="tab-providers">Providers</TabsTrigger>
          <TabsTrigger value="fmos" data-testid="tab-fmos">FMOs</TabsTrigger>
          <TabsTrigger value="producers" data-testid="tab-producers">Producers</TabsTrigger>
          <TabsTrigger value="profiles" data-testid="tab-profiles">Profiles</TabsTrigger>
          <TabsTrigger value="destinations" data-testid="tab-destinations">Destinations</TabsTrigger>
        </TabsList>

        <TabsContent value="tester">
          <RouteTester data={data} />
        </TabsContent>

        {Object.keys(ENTITY_META).map((entity) => (
          <TabsContent key={entity} value={entity}>
            <div className="bg-white rounded-xl border border-mist-200 overflow-hidden mt-4">
              <div className="p-5 border-b border-mist-200 flex items-center justify-between">
                <h3 className="font-display text-lg font-bold text-navy-900">{ENTITY_META[entity].title}</h3>
                <Button size="sm" onClick={() => setDialog({ open: true, entity, initial: null })}
                  className="bg-gold text-navy-900 hover:bg-gold-light rounded-full font-bold" data-testid={`add-${entity}`}>
                  <Plus className="w-4 h-4 mr-1" /> Add {ENTITY_META[entity].singular}
                </Button>
              </div>
              <Table data-testid={`table-${entity}`}>
                <TableHeader><TableRow>{heads[entity].map((h, i) => <TableHead key={i}>{h}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {data[entity].length ? data[entity].map((r) => (
                    <TableRow key={r.id} data-testid={`row-${entity}-${r.id}`}>
                      {columns[entity](r).map((c, j) => (
                        <TableCell key={j} className={j === 0 ? "font-semibold text-navy-900" : "text-muted-foreground"}>{c}</TableCell>
                      ))}
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="icon" onClick={() => setDialog({ open: true, entity, initial: r })} data-testid={`edit-${entity}-${r.id}`}>
                          <Pencil className="w-4 h-4 text-navy-700" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(entity, r.id)} data-testid={`delete-${entity}-${r.id}`}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )) : <TableRow><TableCell colSpan={heads[entity].length} className="text-center text-muted-foreground py-8">No {entity} yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <EntityDialog
        open={dialog.open}
        entity={dialog.entity}
        initial={dialog.initial}
        data={data}
        onOpenChange={(o) => setDialog((s) => ({ ...s, open: o }))}
        onSaved={loadAll}
      />
    </div>
  );
}

// ---------------- Generic entity dialog ----------------
function EntityDialog({ open, entity, initial, data, onOpenChange, onSaved }) {
  const fields = entity ? FIELD_DEFS[entity] : [];
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const editing = !!initial?.id;

  useEffect(() => {
    if (!open || !entity) return;
    const base = {};
    fields.forEach((f) => {
      let v = initial ? initial[f.key] : f.default;
      if (f.type === "csv") v = Array.isArray(initial?.[f.key]) ? initial[f.key].join(", ") : (v ?? "");
      else if (f.type === "multiselect") v = initial?.[f.key] ?? f.default ?? [];
      else if (f.type === "checkbox") v = initial ? !!initial[f.key] : (f.default ?? false);
      else if (f.type === "number") v = initial?.[f.key] ?? f.default ?? 0;
      else v = (v ?? "");
      base[f.key] = v;
    });
    setForm(base);
  }, [open, entity, initial]); // eslint-disable-line

  if (!entity) return null;

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleMulti = (k, val) => setForm((s) => {
    const arr = s[k] || [];
    return { ...s, [k]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
  });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      fields.forEach((f) => {
        const v = form[f.key];
        if (f.type === "csv") payload[f.key] = String(v || "").split(",").map((s) => s.trim()).filter(Boolean);
        else if (f.type === "number") payload[f.key] = parseInt(v || 0, 10);
        else if (f.type === "checkbox") payload[f.key] = !!v;
        else if (f.type === "multiselect") payload[f.key] = v || [];
        else if ((f.type === "ref" || f.type === "select") && !f.required && v === "") payload[f.key] = null;
        else payload[f.key] = v;
      });
      if (editing) await api.put(`/admin/enrollment/${entity}/${initial.id}`, payload);
      else await api.post(`/admin/enrollment/${entity}`, payload);
      toast.success(editing ? "Saved" : "Created");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const refOptions = (refFrom) => (data[refFrom] || []).map((x) => ({
    id: x.id, label: x.name || x.label || x.provider_account_ref || x.id,
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-h-[88vh] overflow-y-auto sm:max-w-lg" data-testid="entity-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {editing ? "Edit" : "Add"} {ENTITY_META[entity].singular}
          </DialogTitle>
          <DialogDescription>Configuration for the provider-neutral enrollment layer.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          {fields.map((f) => (
            <div key={f.key}>
              {f.type !== "checkbox" && <Label className="font-semibold">{f.label}</Label>}
              {f.type === "text" && <Input value={form[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} required={f.required} className="mt-1.5" data-testid={`field-${f.key}`} />}
              {f.type === "textarea" && <Textarea value={form[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} rows={2} className="mt-1.5" data-testid={`field-${f.key}`} />}
              {f.type === "number" && <Input type="number" value={form[f.key] ?? 0} onChange={(e) => set(f.key, e.target.value)} className="mt-1.5" data-testid={`field-${f.key}`} />}
              {f.type === "csv" && <Input value={form[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} className="mt-1.5" placeholder={f.hint} data-testid={`field-${f.key}`} />}
              {f.type === "select" && (
                <Select value={form[f.key] ?? ""} onValueChange={(v) => set(f.key, v)}>
                  <SelectTrigger className="mt-1.5" data-testid={`field-${f.key}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {f.type === "ref" && (
                <Select value={form[f.key] ?? ""} onValueChange={(v) => set(f.key, v)}>
                  <SelectTrigger className="mt-1.5" data-testid={`field-${f.key}`}><SelectValue placeholder={`Select ${f.label}`} /></SelectTrigger>
                  <SelectContent>{refOptions(f.refFrom).map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {f.type === "multiselect" && (
                <div className="flex flex-wrap gap-3 mt-2">
                  {f.options.map((o) => (
                    <label key={o} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`field-${f.key}-${o}`}>
                      <Checkbox checked={(form[f.key] || []).includes(o)} onCheckedChange={() => toggleMulti(f.key, o)} /> {o}
                    </label>
                  ))}
                </div>
              )}
              {f.type === "checkbox" && (
                <label className="flex items-center gap-2 cursor-pointer" data-testid={`field-${f.key}`}>
                  <Checkbox checked={!!form[f.key]} onCheckedChange={(c) => set(f.key, !!c)} />
                  <span className="font-semibold text-sm">{f.label}</span>
                </label>
              )}
              {f.hint && f.type === "csv" && <p className="text-xs text-muted-foreground mt-1">{f.hint}</p>}
            </div>
          ))}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-gold text-navy-900 hover:bg-gold-light font-bold" data-testid="save-entity">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Route Tester ----------------
function RouteTester({ data }) {
  const [ctx, setCtx] = useState({ state: "TX", product_type: "MAPD", carrier: "", producer_id: "", fmo_id: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const payload = {
        state: ctx.state || null, product_type: ctx.product_type || null,
        carrier: ctx.carrier || null, producer_id: ctx.producer_id || null, fmo_id: ctx.fmo_id || null,
      };
      const { data: res } = await api.post("/enrollment/resolve", payload);
      setResult(res);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 mt-4">
      <div className="bg-white rounded-xl border border-mist-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Route className="w-5 h-5 text-gold-dark" />
          <h3 className="font-display text-lg font-bold text-navy-900">Resolve a destination</h3>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-semibold">State</Label>
              <Input value={ctx.state} onChange={(e) => setCtx({ ...ctx, state: e.target.value.toUpperCase() })} className="mt-1.5" maxLength={2} data-testid="tester-state" />
            </div>
            <div>
              <Label className="font-semibold">Product type</Label>
              <Select value={ctx.product_type} onValueChange={(v) => setCtx({ ...ctx, product_type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="tester-product-type"><SelectValue /></SelectTrigger>
                <SelectContent>{PRODUCT_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="font-semibold">Producer <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Select value={ctx.producer_id || "any"} onValueChange={(v) => setCtx({ ...ctx, producer_id: v === "any" ? "" : v })}>
              <SelectTrigger className="mt-1.5" data-testid="tester-producer"><SelectValue placeholder="Any producer" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any producer</SelectItem>
                {(data.producers || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-semibold">Carrier code <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input value={ctx.carrier} onChange={(e) => setCtx({ ...ctx, carrier: e.target.value })} className="mt-1.5" data-testid="tester-carrier" />
          </div>
          <Button onClick={run} disabled={loading} className="w-full bg-navy-800 text-white hover:bg-navy-700 rounded-full font-bold" data-testid="tester-resolve">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Resolve enrollment route"}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-mist-200 p-6" data-testid="tester-result">
        <h3 className="font-display text-lg font-bold text-navy-900 mb-4">Resolution</h3>
        {!result ? (
          <p className="text-sm text-muted-foreground">Run a resolution to see which approved provider & destination applies.</p>
        ) : result.resolved ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-green-700 font-semibold"><CheckCircle2 className="w-5 h-5" /> Approved route found</div>
            <div className="rounded-lg bg-mist-100 p-4 space-y-2 text-sm">
              <Row label="Provider" value={`${result.provider.name} (${result.provider.integration_type})`} />
              <Row label="Account ref" value={result.profile.provider_account_ref || "—"} />
              <Row label="Contracting" value={result.profile.contracting_status} />
              <Row label="Destination" value={result.destination.label} />
              <Row label="API prefill" value={result.prefill_supported ? "Supported" : "Not yet — consumer re-enters at provider"} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Destination URL (configuration/data)</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-navy-900 text-gold rounded-lg px-3 py-2 break-all">{result.destination.url}</code>
                {result.destination.url && (
                  <a href={result.destination.url} target="_blank" rel="noopener noreferrer" data-testid="open-destination">
                    <Button size="icon" className="bg-gold text-navy-900 hover:bg-gold-light shrink-0"><ExternalLink className="w-4 h-4" /></Button>
                  </a>
                )}
              </div>
            </div>
            {result.alternatives?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-navy-700 mb-1">Alternatives</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {result.alternatives.map((a, i) => <li key={i}>{a.provider_code} · {a.label}</li>)}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-destructive font-semibold"><XCircle className="w-5 h-5" /> No approved route</div>
            <p className="text-sm text-muted-foreground">{result.reason}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold text-navy-900 text-right">{value}</span>
    </div>
  );
}
